import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2, UserPlus, X } from 'lucide-react';
import {
  api, BASE_ROLES, LEVEL_LABELS, ROLE_HINTS, ROLE_LABELS,
  type AccessLevel, type BaseRole, type MatrixUser,
} from '../lib/api';
import { R, btnAccent, btnOutline, chip, field, ft, label, levelTone, mono, plural } from '../lib/ui';
import { Avatar } from '../components/Primitives';
import { Select } from '../components/Select';
import { useDialog } from '../components/DialogHost';

const COLUMNS = 'minmax(220px,1fr) 200px 180px 96px';
const COLUMNS_NARROW = 'minmax(160px,1fr) 160px';
const LEVELS: AccessLevel[] = ['owner', 'editor', 'viewer', 'none'];

function useNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(
    typeof window === 'undefined' ? false : window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

function ScopeRow({
  name, count, active, onClick,
}: {
  name: string;
  count: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 18px',
        textAlign: 'left',
        fontSize: 12.5,
        borderLeft: `3px solid ${active ? R.accent : 'transparent'}`,
        background: active ? R.accentSubtle : 'transparent',
        color: active ? R.text : R.textSecondary,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      <span
        style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {name}
      </span>
      <span style={{ fontFamily: mono, fontSize: 11, color: R.textMuted }}>{count}</span>
    </button>
  );
}

export function AccessPage() {
  const qc = useQueryClient();
  const dialog = useDialog();
  const narrow = useNarrow();

  const [scope, setScope] = useState<string>('org');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLogin, setNewLogin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<BaseRole>('viewer');

  const me = useQuery({ queryKey: ['me'], queryFn: api.me }).data;
  const matrix = useQuery({ queryKey: ['access'], queryFn: api.access });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['access'] });
    qc.invalidateQueries({ queryKey: ['docs'] });
  };

  const onError = (e: unknown) =>
    void dialog.alert({ title: 'Не получилось', body: (e as Error).message });

  const setRole = useMutation({
    mutationFn: (v: { userId: string; role: BaseRole | null }) =>
      api.accessSetRole(v.userId, v.role),
    onSuccess: invalidate,
    onError,
  });

  const setSection = useMutation({
    mutationFn: (v: { sectionId: string; userId: string; level: AccessLevel | null }) =>
      api.accessSetSection(v.sectionId, v.userId, v.level),
    onSuccess: invalidate,
    onError,
  });

  const setDoc = useMutation({
    mutationFn: (v: { docId: string; userId: string; level: AccessLevel | null }) =>
      api.accessSetDoc(v.docId, v.userId, v.level),
    onSuccess: invalidate,
    onError,
  });

  const addUser = useMutation({
    mutationFn: (param: {
      userId?: string;
      login?: string;
      password?: string;
      name?: string;
      role: BaseRole;
    }) => api.accessAddUser(param),
    onSuccess: (added) => {
      setAdding(false);
      setNewName('');
      setNewLogin('');
      setNewPassword('');
      invalidate();
      if (!added.resolved) {
        dialog.alert({
          title: 'Добавлен',
          body: `Пользователь ${added.name} добавлен в матрицу.`,
        });
      }
    },
    onError,
  });

  const removeUser = useMutation({
    mutationFn: (userId: string) => api.accessRemoveUser(userId),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
    onError,
  });

  const data = matrix.data;
  const users = data?.users ?? [];
  const sections = data?.sections ?? [];
  const docs = data?.docs ?? [];

  const sectionLevels = useMemo(() => {
    const map = new Map<string, AccessLevel>();
    for (const a of data?.sectionAccess ?? []) map.set(`${a.sectionId}:${a.userId}`, a.level);
    return map;
  }, [data]);

  const exceptions = useMemo(() => {
    const map = new Map<string, { docId: string; docName: string; level: AccessLevel }[]>();
    for (const a of data?.docAccess ?? []) {
      const list = map.get(a.userId) ?? [];
      list.push({ docId: a.docId, docName: a.docName, level: a.level });
      map.set(a.userId, list);
    }
    return map;
  }, [data]);

  const selected = users.find((u) => u.id === selectedId) ?? users[0] ?? null;

  if (me && me.role !== 'admin') {
    return (
      <div style={{ padding: '40px 28px', color: R.textMuted }}>
        Матрица доступа доступна только администратору.
      </div>
    );
  }

  const scopeName =
    scope === 'org'
      ? 'Вся организация'
      : sections.find((s) => s.id === scope)?.name ?? 'Раздел';

  const askRemove = async (user: MatrixUser) => {
    const ok = await dialog.confirm({
      title: 'Убрать из матрицы?',
      body:
        `${user.name} потеряет все выданные права и исчезнёт из списка. ` +
        'При следующем входе ему потребуется заново выдать права.',
      danger: true,
      confirmLabel: 'Убрать',
    });
    if (ok) removeUser.mutate(user.id);
  };

  const grid = narrow ? COLUMNS_NARROW : COLUMNS;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: R.bg }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          padding: '24px 28px 16px',
          borderBottom: ft.strong,
          flex: '0 0 auto',
        }}
      >
        <div>
          <div style={label}>Организация · GTA5RP</div>
          <h2 style={{ margin: '2px 0 0', fontSize: 30, color: R.text }}>Матрица доступа</h2>
        </div>

        <span
          style={{ fontSize: 12, fontFamily: mono, color: R.textMuted, paddingBottom: 6 }}
        >
          {users.length} польз. · {sections.length} разд. · {docs.length} докум.
        </span>

        <button
          style={{ ...btnAccent, marginLeft: 'auto', alignSelf: 'center' }}
          onClick={() => setAdding((v) => !v)}
        >
          <UserPlus size={14} /> Добавить пользователя
        </button>
      </header>

      {adding && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '12px 28px',
            borderBottom: ft.hair,
            background: R.bgSubtle,
            position: 'relative',
          }}
        >
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Имя пользователя"
            style={{ ...field, width: 200, boxSizing: 'border-box' }}
          />
          <input
            value={newLogin}
            onChange={(e) => setNewLogin(e.target.value)}
            placeholder="Логин"
            style={{ ...field, width: 160, boxSizing: 'border-box' }}
          />
          <input
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Временный пароль"
            style={{ ...field, width: 160, boxSizing: 'border-box' }}
          />

          <span style={{ width: 178 }}>
            <Select
              value={newRole}
              onChange={setNewRole}
              options={BASE_ROLES.map((r) => ({
                value: r,
                label: ROLE_LABELS[r],
                hint: ROLE_HINTS[r],
              }))}
            />
          </span>

          <button
            style={{
              ...btnAccent,
              opacity: (newName.trim() && newLogin.trim() && newPassword.trim()) && !addUser.isPending ? 1 : 0.5,
            }}
            disabled={!newName.trim() || !newLogin.trim() || !newPassword.trim() || addUser.isPending}
            onClick={() => {
              addUser.mutate({
                name: newName.trim(),
                login: newLogin.trim(),
                password: newPassword.trim(),
                role: newRole,
              });
            }}
          >
            {addUser.isPending ? 'Создаём…' : 'Создать'}
          </button>

          <button
            style={btnOutline}
            onClick={() => {
              setAdding(false);
              setNewName('');
              setNewLogin('');
              setNewPassword('');
            }}
          >
            Отмена
          </button>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          padding: '6px 28px',
          borderBottom: ft.hair,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ ...label, marginRight: 2 }}>Уровни</span>
        {LEVELS.map((level) => {
          const tone = levelTone(level);
          return (
            <span
              key={level}
              style={{
                ...chip(false),
                cursor: 'default',
                background: tone.bg,
                color: tone.fg,
                borderColor: tone.line,
              }}
            >
              {LEVEL_LABELS[level]}
            </span>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: R.textMuted }}>
          Показана область: {scopeName}
        </span>
      </div>

      {narrow && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px 28px',
            borderBottom: ft.hair,
          }}
        >
          <button style={chip(scope === 'org')} onClick={() => setScope('org')}>
            Вся организация
          </button>
          {sections.map((s) => (
            <button key={s.id} style={chip(scope === s.id)} onClick={() => setScope(s.id)}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {matrix.isLoading && (
        <div style={{ padding: '24px 28px', color: R.textMuted, fontSize: 13 }}>Загрузка…</div>
      )}
      {matrix.error && (
        <div style={{ padding: '24px 28px', color: R.danger, fontSize: 13 }}>
          {(matrix.error as Error).message}
        </div>
      )}

      {data && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div
            style={{
              display: narrow ? 'none' : 'flex',
              width: 196,
              flex: '0 0 196px',
              borderRight: ft.hair,
              flexDirection: 'column',
              background: R.bg,
            }}
          >
            <div style={{ ...label, padding: '14px 18px 8px' }}>Область доступа</div>
            <ScopeRow
              name="Вся организация"
              count={String(users.length)}
              active={scope === 'org'}
              onClick={() => setScope('org')}
            />
            {sections.map((s) => (
              <ScopeRow
                key={s.id}
                name={s.name}
                count={String(s.docCount)}
                active={scope === s.id}
                onClick={() => setScope(s.id)}
              />
            ))}
            <div style={{ flex: 1 }} />
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRight: ft.strong,
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: grid,
                minHeight: 52,
                padding: '0 18px',
                alignItems: 'center',
                background: R.bgSubtle,
                borderBottom: ft.strong,
                ...label,
                letterSpacing: '.12em',
              }}
            >
              <span>Пользователь</span>
              <span>{scope === 'org' ? 'Базовая роль' : 'Доступ к разделу'}</span>
              {!narrow && (
                <>
                  <span>Исключения</span>
                  <span>Вход</span>
                </>
              )}
            </div>

            <div className="rt-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {users.map((user) => {
                const own = exceptions.get(user.id) ?? [];
                const level = scope === 'org' ? null : sectionLevels.get(`${scope}:${user.id}`) ?? null;
                const isSelf = user.id === me?.id;

                return (
                  <div
                    key={user.id}
                    onClick={() => {
                      setSelectedId(user.id);
                      if (narrow) setPanelOpen(true);
                    }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: grid,
                      minHeight: 58,
                      padding: '8px 18px',
                      alignItems: 'center',
                      borderBottom: ft.hair,
                      background: selected?.id === user.id ? R.selectionSubtle : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minWidth: 0,
                        paddingRight: 12,
                      }}
                    >
                      <Avatar name={user.name} src={user.avatarUrl} size={28} />
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          lineHeight: 1.25,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: R.text,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {user.name}
                        </span>
                        <span style={{ fontSize: 11, color: R.textMuted }}>
                          {user.login ? `Логин: ${user.login}` : 'Системный'}
                          {user.roleOverride ? ' · роль задана вручную' : ''}
                        </span>
                      </span>
                    </span>

                    <span onClick={(e) => e.stopPropagation()} style={{ paddingRight: 12 }}>
                      {scope === 'org' ? (
                        <Select
                          value={user.role}
                          disabled={isSelf || setRole.isPending}
                          onChange={(role) => setRole.mutate({ userId: user.id, role })}
                          options={BASE_ROLES.map((r) => ({
                            value: r,
                            label: ROLE_LABELS[r],
                            hint: ROLE_HINTS[r],
                          }))}
                        />
                      ) : (
                        <Select
                          value={level ?? 'inherit'}
                          disabled={setSection.isPending}
                          onChange={(v) =>
                            setSection.mutate({
                              sectionId: scope,
                              userId: user.id,
                              level: v === 'inherit' ? null : (v as AccessLevel),
                            })
                          }
                          options={[
                            {
                              value: 'inherit',
                              label: 'Как базовая роль',
                              hint: ROLE_LABELS[user.role],
                            },
                            ...LEVELS.map((l) => ({ value: l, label: LEVEL_LABELS[l] })),
                          ]}
                        />
                      )}
                    </span>

                    {!narrow && (
                      <>
                        <span
                          title={own.map((x) => `${x.docName} — ${LEVEL_LABELS[x.level]}`).join('\n')}
                          style={{
                            fontSize: 12,
                            color: R.textMuted,
                            paddingRight: 12,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {own.length === 0
                            ? '—'
                            : own.length === 1
                              ? `${own[0].docName} · ${LEVEL_LABELS[own[0].level]}`
                              : `${own.length} ${plural(own.length, 'документ', 'документа', 'документов')}`}
                        </span>
                        <span style={{ fontSize: 12, color: R.textMuted }}>
                          {user.lastSeenAt
                            ? new Date(user.lastSeenAt).toLocaleDateString('ru')
                            : 'не входил'}
                        </span>
                      </>
                    )}
                  </div>
                );
              })}

              {users.length === 0 && (
                <div style={{ padding: '24px 18px', color: R.textMuted, fontSize: 12.5 }}>
                  Пока никого нет — добавьте пользователя по Discord ID.
                </div>
              )}
            </div>
          </div>

          {selected && (!narrow || panelOpen) && (
            <UserPanel
              user={selected}
              exceptions={exceptions.get(selected.id) ?? []}
              freeDocs={docs.filter(
                (d) => !(exceptions.get(selected.id) ?? []).some((e) => e.docId === d.id),
              )}
              isSelf={selected.id === me?.id}
              narrow={narrow}
              onSetRole={(role) => setRole.mutate({ userId: selected.id, role })}
              onSetDoc={(docId, level) => setDoc.mutate({ docId, userId: selected.id, level })}
              onRemove={() => askRemove(selected)}
              onClose={narrow ? () => setPanelOpen(false) : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

function UserPanel({
  user, exceptions, freeDocs, isSelf, narrow, onSetRole, onSetDoc, onRemove, onClose,
}: {
  user: MatrixUser;
  exceptions: { docId: string; docName: string; level: AccessLevel }[];
  freeDocs: { id: string; name: string }[];
  isSelf: boolean;
  narrow: boolean;
  onSetRole: (role: BaseRole | null) => void;
  onSetDoc: (docId: string, level: AccessLevel | null) => void;
  onRemove: () => void;
  onClose?: () => void;
}) {
  const [picking, setPicking] = useState(false);

  return (
    <aside
      className="rt-scroll"
      style={{
        width: narrow ? '100%' : 300,
        flex: narrow ? 1 : '0 0 300px',
        background: R.bgSubtle,
        borderLeft: ft.strong,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          padding: '16px 18px',
          borderBottom: ft.strong,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Avatar name={user.name} src={user.avatarUrl} size={32} />
        <span
          style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: R.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user.name}
          </span>
          <span style={{ fontSize: 11, color: R.textMuted }}>
            {user.login ? `Логин: ${user.login}` : 'Без логина'}
            {user.lastSeenAt
              ? ` · ${new Date(user.lastSeenAt).toLocaleDateString('ru')}`
              : ' · не входил'}
          </span>
        </span>

        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          {!isSelf && (
            <button
              onClick={onRemove}
              title="Убрать из матрицы"
              style={{
                width: 26,
                height: 26,
                display: 'grid',
                placeItems: 'center',
                color: R.danger,
                background: 'transparent',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = R.dangerSubtle)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Trash2 size={14} />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              title="Закрыть"
              style={{
                width: 26,
                height: 26,
                display: 'grid',
                placeItems: 'center',
                color: R.textMuted,
                background: 'transparent',
              }}
            >
              <X size={15} />
            </button>
          )}
        </span>
      </div>

      <div
        style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div>
          <div style={{ ...label, marginBottom: 6 }}>Базовая роль</div>

          <div style={{ display: 'flex', flexDirection: 'column', border: ft.edge }}>
            {BASE_ROLES.map((role) => {
              const active = user.role === role;
              return (
                <button
                  key={role}
                  disabled={isSelf}
                  onClick={() => onSetRole(role)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 10px',
                    borderBottom: ft.hair,
                    textAlign: 'left',
                    background: active ? R.accentSubtle : 'transparent',
                    cursor: isSelf ? 'default' : 'pointer',
                    opacity: isSelf && !active ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      flex: '0 0 10px',
                      marginTop: 4,
                      border: `1px solid ${active ? R.accent : R.border}`,
                      background: active ? R.accent : 'transparent',
                    }}
                  />
                  <span
                    style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}
                  >
                    <span
                      style={{ fontSize: 12.5, fontWeight: active ? 700 : 400, color: R.text }}
                    >
                      {ROLE_LABELS[role]}
                    </span>
                    <span style={{ fontSize: 11, color: R.textMuted }}>{ROLE_HINTS[role]}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {isSelf ? (
            <div style={{ fontSize: 11.5, color: R.textMuted, marginTop: 6 }}>
              Свою роль менять нельзя — иначе можно остаться без администратора.
            </div>
          ) : user.roleOverride ? (
            <button
              onClick={() => onSetRole(null)}
              style={{ ...btnOutline, height: 28, marginTop: 8, fontSize: 12 }}
            >
              Вернуть роль из Discord
            </button>
          ) : (
            <div style={{ fontSize: 11.5, color: R.textMuted, marginTop: 6 }}>
              Роль выводится из ролей Discord при каждом входе.
            </div>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span style={label}>Исключения по таблицам</span>
            <button
              onClick={() => setPicking((v) => !v)}
              disabled={freeDocs.length === 0}
              title={freeDocs.length === 0 ? 'Исключения заданы для всех документов' : undefined}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11.5,
                color: R.accentText,
                padding: 0,
                background: 'transparent',
                opacity: freeDocs.length === 0 ? 0.4 : 1,
              }}
            >
              <Plus size={12} /> добавить
            </button>
          </div>

          {picking && freeDocs.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Select
                value={null}
                placeholder="Выберите документ"
                onChange={(docId) => {
                  onSetDoc(docId, 'viewer');
                  setPicking(false);
                }}
                options={freeDocs.map((d) => ({ value: d.id, label: d.name }))}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {exceptions.length === 0 && (
              <div style={{ fontSize: 12, color: R.textMuted }}>
                Нет — доступ определяется разделом и базовой ролью.
              </div>
            )}

            {exceptions.map((item) => (
              <div
                key={item.docId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  border: ft.edge,
                  background: R.bg,
                }}
              >
                <span
                  title={item.docName}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12.5,
                    color: R.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.docName}
                </span>

                <span style={{ flex: '0 0 116px' }}>
                  <Select
                    compact
                    value={item.level}
                    onChange={(level) => onSetDoc(item.docId, level)}
                    options={LEVELS.map((l) => ({ value: l, label: LEVEL_LABELS[l] }))}
                  />
                </span>

                <button
                  onClick={() => onSetDoc(item.docId, null)}
                  title="Убрать исключение"
                  style={{
                    width: 20,
                    height: 20,
                    display: 'grid',
                    placeItems: 'center',
                    color: R.textMuted,
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = R.danger)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = R.textMuted)}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
